import type { RefObject, MouseEvent, PointerEvent } from 'react';
import { ActionIcon, Tooltip } from '@mantine/core';
import { IconZoomReset } from '@tabler/icons-react';
import type { ModelViewerElement } from '@google/model-viewer';
import { useTranslation } from 'react-i18next';
import {
  resetModelViewerCamera,
  type ModelViewerCameraDefaults,
} from '@/utils/modelViewerZoom';
import { VIEWER_CONTROL_CHROME_STYLE } from './viewerControlChrome';

const RESET_BUTTON_CORNERS = {
  'top-left': { top: 12, left: 12 },
  'top-right': { top: 12, right: 12 },
} as const;

type ModelViewerResetZoomButtonProps = {
  modelViewerRef: RefObject<ModelViewerElement | null>;
  /** Saved product framing — falls back to model-viewer `auto` when omitted. */
  cameraDefaults?: ModelViewerCameraDefaults;
  placement?: 'top-left' | 'top-right';
  zIndex?: number;
};

export function ModelViewerResetZoomButton({
  modelViewerRef,
  cameraDefaults,
  placement = 'top-left',
  zIndex = 11,
}: ModelViewerResetZoomButtonProps) {
  const { t } = useTranslation();

  const handleReset = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const mv = modelViewerRef.current;
    if (!mv) return;
    resetModelViewerCamera(mv, cameraDefaults);
  };

  return (
    <Tooltip label={t('modelViewer.resetZoom')} withArrow position="bottom">
      <ActionIcon
        variant="default"
        radius="xl"
        size={32}
        aria-label={t('modelViewer.resetZoom')}
        data-annotation-keep-open
        onClick={handleReset}
        onPointerDown={(event: PointerEvent<HTMLButtonElement>) => event.stopPropagation()}
        style={{
          position: 'absolute',
          ...RESET_BUTTON_CORNERS[placement],
          zIndex,
          ...VIEWER_CONTROL_CHROME_STYLE,
        }}
      >
        <IconZoomReset size={16} stroke={2} />
      </ActionIcon>
    </Tooltip>
  );
}
